import { useState } from 'react';
import type { DatosTarifa, NivelTarifa, Tarifa } from '../../../api/ops/listas';
import { Boton } from '../../../componentes/Boton';
import { Campo } from '../../../componentes/Campo';
import { Desplegable } from '../../../componentes/Desplegable';
import { OpcionRadio } from '../../../componentes/OpcionRadio';
import { PanelLateral } from '../../../componentes/PanelLateral';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { textoDeCentimos } from '../../../logica/dinero';
import { usePermisos } from '../../../permisos/permisos';
import { Bloque, Botones, Caja, Dato } from '../comun';
import { codigoDe, useAvisarError, useCarga } from '../uso';
import { BASE, comoSeCalcula, cuandoEs, erroresTarifa, eventoAbierto, NIVEL, nombreRrpp, opsDe, ORDEN_NIVELES, paraQuien, PERMISOS, tarifaDeFormulario, type FormularioTarifa } from '../textos';
import type { ContextoRrpp } from './ApartadoRrpp';

/**
 * Tarifa de comisión (10.10): nueva (`createCommissionRate`) en un escalón (casa, RRPP, evento o lista), cambiarla como
 * versión nueva (`replaceCommissionRate`: no recalcula lo ya calculado ni lo liquidado) o quitarla
 * (`archiveCommissionRate`: vuelve a mandar el escalón de arriba). Importe fijo por entrada, por reserva o por persona
 * (G520), porcentaje de la venta o tramos de la noche (solo RRPP y casa).
 */
const AYUDA_NIVEL: Readonly<Record<NivelTarifa, string>> = { house: 'Cualquier RRPP', promoter: 'Un RRPP concreto', event: 'Todos en un evento', list: 'Todos en una lista' };
const POR_CADA: Readonly<Record<DatosTarifa['basis'], string>> = { guest_arrival: 'Por cada persona que entra', ticket_sold: 'Por cada entrada', ticket_arrival: 'Por cada entrada que entra', reservation: 'Por cada reserva' };

function formularioDe(t: Tarifa | null): FormularioTarifa {
    return {
        nivel: t?.level ?? 'house',
        rrpp: t?.promoter_id ?? '',
        evento: t?.event_id ?? '',
        lista: t?.guest_list_id ?? '',
        base: t?.basis ?? 'guest_arrival',
        como: t?.percent_bps != null ? 'pct' : t?.tiers != null && t.tiers.length > 0 ? 'tiers' : 'amount',
        importe: textoDeCentimos(t?.amount?.amount, true),
        porcentaje: t?.percent_bps == null ? '' : String(t.percent_bps / 100).replace('.', ','),
        tramos: t?.tiers?.map((x) => ({ desde: String(x.from), hasta: x.to == null ? '' : String(x.to), importe: textoDeCentimos(x.amount.amount) })) ?? [{ desde: '1', hasta: '', importe: '' }],
    };
}

export function PanelTarifa({ ctx, tarifa }: { ctx: ContextoRrpp; tarifa: Tarifa | null }) {
    const { props, rrpp, eventos } = ctx;
    const { negocio, movil, soloLectura } = props;
    const ops = opsDe(props.ops);
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const avisarError = useAvisarError();
    const [f, setF] = useState<FormularioTarifa>(() => formularioDe(tarifa));
    const [probado, setProbado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [errorApi, setErrorApi] = useState<string | null>(null);
    const puede = permisos.puede(PERMISOS.tarifas) && !soloLectura;
    const listas = useCarga(f.nivel === 'list' && f.evento !== '' ? () => ops.listGuestLists(negocio, f.evento) : null, [negocio, f.nivel, f.evento]);
    const errores = probado ? erroresTarifa(f) : {};
    const conTramos = f.nivel === 'promoter' || f.nivel === 'house';
    const cambiar = (c: Partial<FormularioTarifa>) => {
        setF({ ...f, ...c });
        setErrorApi(null);
    };
    const moneda = tarifa?.amount?.currency ?? rrpp[0]?.pending_total?.currency ?? 'EUR';

    const guardar = () => {
        if (Object.keys(erroresTarifa(f)).length > 0) {
            setProbado(true);
            return;
        }
        setOcupado(true);
        const datos = tarifaDeFormulario(f, moneda);
        (tarifa === null ? ops.createCommissionRate(negocio, datos) : ops.replaceCommissionRate(negocio, tarifa.id, datos))
            .then(() => {
                avisar({ tipo: 'ok', texto: tarifa === null ? 'Tarifa creada.' : 'Tarifa cambiada. Vale para las comisiones que se calculen desde ahora; lo ya calculado no cambia.' });
                ctx.recargar();
                ctx.abrir(null);
            })
            .catch((e: unknown) => {
                if (codigoDe(e) === 'conflict') {
                    setErrorApi('Ya hay una tarifa así: cámbiala en vez de crear otra.');
                } else {
                    avisarError(e);
                }
            })
            .finally(() => { setOcupado(false); });
    };

    const quitar = () => {
        if (tarifa === null) {
            return;
        }
        setOcupado(true);
        ops.archiveCommissionRate(negocio, tarifa.id)
            .then(() => {
                avisar({ tipo: 'ok', texto: 'Tarifa quitada: vuelve a mandar el escalón de arriba.' });
                ctx.recargar();
                ctx.abrir(null);
            })
            .catch((e: unknown) => { avisarError(e); })
            .finally(() => { setOcupado(false); });
    };

    const para = tarifa === null ? null : paraQuien(tarifa, ctx);
    return (
        <PanelLateral
            abierto
            onCerrar={() => { ctx.abrir(null); }}
            movil={movil}
            titulo={para?.[0] ?? 'Nueva tarifa'}
            eyebrow={tarifa === null ? 'Comisión' : `Comisión · ${NIVEL[tarifa.level][0]}`}
            subtitulo={tarifa === null ? 'Un escalón de la escalera de comisiones.' : `${BASE[tarifa.basis][0]} · ${comoSeCalcula(tarifa)[0]}`}
            icono="percent"
            accion={puede ? (tarifa === null ? 'Crear tarifa' : 'Guardar versión nueva') : ''}
            accionIcono={tarifa === null ? 'plus' : 'check'}
            ocupado={ocupado}
            onAccion={guardar}
        >
            <div className="rv-ca-cuerpo">
                {!puede ? (
                    tarifa !== null && para !== null && (
                        <Bloque>
                            <Dato icono={NIVEL[tarifa.level][1]} k="Para" v={para[0]} sub={para[1]} />
                            <Dato icono={BASE[tarifa.basis][1]} k="Por cada" v={BASE[tarifa.basis][0]} />
                            <Dato icono="percent" k="Comisión" v={comoSeCalcula(tarifa)[0]} sub={comoSeCalcula(tarifa)[1]} />
                            <Caja icono="eye">Las comisiones las cambia el propietario o un administrador.</Caja>
                        </Bloque>
                    )
                ) : (
                    <>
                        {tarifa === null ? (
                            <Bloque titulo="Para quién">
                                <div className={`rv-ls-opciones${movil ? ' rv-ls-opciones--movil' : ''}`} role="radiogroup" aria-label="Para quién">
                                    {[...ORDEN_NIVELES].reverse().map((k) => (
                                        <OpcionRadio key={k} elegida={f.nivel === k} onElegir={() => { cambiar({ nivel: k, como: k !== 'promoter' && k !== 'house' && f.como === 'tiers' ? 'amount' : f.como }); }} titulo={NIVEL[k][0]} texto={AYUDA_NIVEL[k]} icono={NIVEL[k][1]} />
                                    ))}
                                </div>
                                {f.nivel === 'promoter' && <Desplegable etiqueta="RRPP" valor={f.rrpp} onChange={(v) => { cambiar({ rrpp: v }); }} error={errores.rrpp} opciones={[{ valor: '', texto: 'Elige un RRPP' }, ...rrpp.map((p) => ({ valor: p.id, texto: nombreRrpp(p) }))]} />}
                                {(f.nivel === 'event' || f.nivel === 'list') && (
                                    <Desplegable etiqueta="Evento" valor={f.evento} onChange={(v) => { cambiar({ evento: v, lista: '' }); }} error={errores.evento} opciones={[{ valor: '', texto: 'Elige un evento' }, ...eventos.filter((e) => eventoAbierto(e)).map((e) => ({ valor: e.id, texto: `${e.name ?? ''} · ${cuandoEs(e.starts_at)}` }))]} />
                                )}
                                {f.nivel === 'list' && f.evento !== '' && (
                                    <Desplegable etiqueta="Lista" valor={f.lista} onChange={(v) => { cambiar({ lista: v }); }} error={errores.lista} opciones={[{ valor: '', texto: 'Elige una lista' }, ...(listas.datos?.data ?? []).filter((l) => l.status !== 'cancelled').map((l) => ({ valor: l.id, texto: l.name ?? 'Lista' }))]} />
                                )}
                            </Bloque>
                        ) : (
                            para !== null && <Bloque><Dato icono={NIVEL[tarifa.level][1]} k="Para" v={para[0]} sub={para[1]} /></Bloque>
                        )}
                        <Bloque titulo="Cómo se calcula">
                            {tarifa === null ? (
                                <Desplegable etiqueta="Por cada" valor={f.base} onChange={(v) => { cambiar({ base: v as DatosTarifa['basis'] }); }} opciones={Object.entries(BASE).map(([k, v]) => ({ valor: k, texto: v[0] }))} />
                            ) : (
                                <Dato icono={BASE[f.base][1]} k="Por cada" v={BASE[f.base][0]} />
                            )}
                            <div className={`rv-ls-opciones${movil ? ' rv-ls-opciones--movil' : ''}`} role="radiogroup" aria-label="Cómo se calcula">
                                <OpcionRadio elegida={f.como === 'amount'} onElegir={() => { cambiar({ como: 'amount' }); }} titulo="Importe fijo" texto={POR_CADA[f.base]} icono="euro" />
                                <OpcionRadio elegida={f.como === 'pct'} onElegir={() => { cambiar({ como: 'pct' }); }} titulo="Porcentaje" texto="De la venta" icono="percent" />
                                {conTramos && <OpcionRadio elegida={f.como === 'tiers'} onElegir={() => { cambiar({ como: 'tiers' }); }} titulo="Tramos" texto="Según la noche" icono="layers" />}
                            </div>
                            {f.como === 'amount' && <Campo etiqueta="Importe (€)" valor={f.importe} onChange={(v) => { cambiar({ importe: v }); }} modo="decimal" error={errores.importe ?? errorApi} ayuda={f.base === 'ticket_sold' || f.base === 'reservation' ? 'Por cada venta.' : 'Por cada persona.'} />}
                            {f.como === 'pct' && <Campo etiqueta="Porcentaje de la venta (%)" valor={f.porcentaje} onChange={(v) => { cambiar({ porcentaje: v }); }} modo="decimal" error={errores.porcentaje ?? errorApi} ayuda="Sin contar los gastos de gestión." />}
                            {f.como === 'tiers' && (
                                <>
                                    {f.tramos.map((t, i) => {
                                        const ultimo = i === f.tramos.length - 1;
                                        const desde = i === 0 ? '1' : String(Number(f.tramos[i - 1]?.hasta ?? 0) + 1);
                                        return (
                                            <div key={String(i)} className="rv-ls-tramo">
                                                <span className="rv-ls-tramo__desde">Desde {desde}</span>
                                                {ultimo ? (
                                                    <span className="rv-ls-tramo__desde">en adelante</span>
                                                ) : (
                                                    <Campo etiqueta="Hasta" valor={t.hasta} modo="numeric" onChange={(v) => { cambiar({ tramos: f.tramos.map((x, j) => (j === i ? { ...x, hasta: v } : x)) }); }} />
                                                )}
                                                <Campo etiqueta="Importe (€)" valor={t.importe} modo="decimal" error={errores[`tramo${String(i)}`]} onChange={(v) => { cambiar({ tramos: f.tramos.map((x, j) => (j === i ? { ...x, importe: v } : x)) }); }} />
                                                {f.tramos.length > 1 && (
                                                    <button type="button" className="rv-ls-fantasma" aria-label={`Quitar el tramo ${String(i + 1)}`} onClick={() => { cambiar({ tramos: f.tramos.filter((_, j) => j !== i) }); }}>
                                                        <span aria-hidden="true">×</span>
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })}
                                    {f.tramos.length < 10 && (
                                        <button
                                            type="button"
                                            className="rv-ls-mas"
                                            onClick={() => {
                                                const tramos = f.tramos.map((x) => ({ ...x }));
                                                const ultimo = tramos[tramos.length - 1];
                                                if (ultimo !== undefined && ultimo.hasta === '') {
                                                    const desde = tramos.length === 1 ? 1 : Number(tramos[tramos.length - 2]?.hasta ?? 0) + 1;
                                                    ultimo.hasta = String(desde + 9);
                                                }
                                                tramos.push({ desde: '', hasta: '', importe: '' });
                                                cambiar({ tramos });
                                            }}
                                        >
                                            + Añadir un tramo
                                        </button>
                                    )}
                                    {errorApi !== null && <Caja icono="circle-alert" tono="error">{errorApi}</Caja>}
                                    <Caja icono="info">Al terminar la noche se cuenta cuántos entraron con él: el tramo alcanzado vale para todos los de esa noche.</Caja>
                                </>
                            )}
                        </Bloque>
                        {tarifa !== null && (
                            <>
                                <Caja icono="history">Se guarda como versión nueva: no recalcula las comisiones ya calculadas ni las liquidadas.</Caja>
                                <Botones nota="Vuelve a mandar el escalón de arriba.">
                                    <Boton tipo="destructivo" icono="archive" onClick={quitar} disabled={ocupado}>Quitar tarifa</Boton>
                                </Botones>
                            </>
                        )}
                    </>
                )}
            </div>
        </PanelLateral>
    );
}
